import { ElementGroupBucket } from '../hooks/useElementGroupBuckets'
import { DamageState } from '../types/riskProfileSelected.types'
import type {
  MultiHazardExposureData,
  VulnerabilityChartItem,
  VulnerabilityCountByLevel,
  VulnerabilityDisplayLabel,
  VulnerabilityLevel,
} from '../types/riskSummary.types'

// ─── Vulnerability display mapping ────────────────────────────────────────────

/**
 * Maps a normalised VulnerabilityLevel to its display label.
 * isMinorMajor controls whether "low" renders as "Minor" (true) or "Low" (false),
 * and "high" renders as "Major" (true) or "High" (false).
 */

export function getVulnerabilityDisplayLabel(
  key: VulnerabilityLevel,
  isMinorMajor: boolean,
): VulnerabilityDisplayLabel {
  const map: Record<
    VulnerabilityLevel,
    { minorMajor: VulnerabilityDisplayLabel; lowHigh: VulnerabilityDisplayLabel }
  > = {
    low: { minorMajor: 'Minor', lowHigh: 'Low' },
    medium: { minorMajor: 'Moderate', lowHigh: 'Moderate' },
    high: { minorMajor: 'Major', lowHigh: 'High' },
    potential: { minorMajor: 'Potential', lowHigh: 'Potential' },
    exposed: { minorMajor: 'Exposed', lowHigh: 'Exposed' },
    unspecified: { minorMajor: 'Unspecified', lowHigh: 'Unspecified' },
    insignificant: { minorMajor: 'Insignificant', lowHigh: 'Insignificant' },
  }
  return isMinorMajor ? map[key].minorMajor : map[key].lowHigh
}

/** Display order for vulnerability levels — higher = more severe */
export const VULNERABILITY_ORDER: Record<VulnerabilityLevel, number> = {
  unspecified: 0,
  insignificant: 1,
  low: 2,
  medium: 3,
  high: 4,
  exposed: 5,
  potential: 6,
}

const VULNERABILITY_LEVELS: VulnerabilityLevel[] = [
  'insignificant',
  'low',
  'medium',
  'high',
  'potential',
  'exposed',
  'unspecified',
]

/**
 * Converts MultiHazardExposureData fields into chart-ready VulnerabilityChartItems.
 * Raw field names have "_vulnerability" suffix — stripped to get the normalised key.
 * Zero-value items are filtered out.
 */
export function multiHazardDataToChartItems(
  data: MultiHazardExposureData,
  palette: Record<string, string>,
  isMinorMajor: boolean,
): VulnerabilityChartItem[] {
  return VULNERABILITY_LEVELS.map((key) => ({
    key,
    label: getVulnerabilityDisplayLabel(key, isMinorMajor),
    value: data[`${key}_vulnerability` as keyof MultiHazardExposureData] as number,
    color: palette[key] ?? '#ccc',
    order: VULNERABILITY_ORDER[key],
  }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.order - a.order)
}

/**
 * Converts VulnerabilityCountByLevel (from HazardExposureData.data.totals)
 * into chart-ready VulnerabilityChartItems.
 * Raw field names have "_vulnerability" suffix — stripped to get the normalised key.
 * Zero-value items are filtered out.
 */
export function vulnerabilityCountsToChartItems(
  totals: VulnerabilityCountByLevel,
  palette: Record<string, string>,
  isMinorMajor: boolean,
): VulnerabilityChartItem[] {
  return VULNERABILITY_LEVELS.map((key) => ({
    key,
    label: getVulnerabilityDisplayLabel(key, isMinorMajor),
    value: totals[`${key}_vulnerability` as keyof VulnerabilityCountByLevel] as number,
    color: palette[key] ?? '#ccc',
    order: VULNERABILITY_ORDER[key],
  }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.order - a.order)
}

// ─── Percent exposed ──────────────────────────────────────────────────────────

/**
 * Percent exposed from MultiHazardExposureData.
 * total_metric_exposed / total_metric × 100
 */
export function calcMultiHazardExposedPct(data: MultiHazardExposureData): number {
  if (!data.total_metric || data.total_metric === 0) return 0
  return (data.total_metric_exposed / data.total_metric) * 100
}

/**
 * Percent exposed from per-hazard data.
 * totals.total / total_metric × 100
 * totals.total = total exposed assets (from VulnerabilityCountByLevel)
 */
export function calcPerHazardExposedPct(totalExposed: number, totalMetric: number): number {
  if (!totalMetric || totalMetric === 0) return 0
  return (totalExposed / totalMetric) * 100
}

// ─── Formatting ───────────────────────────────────────────────────────────────

export function formatMetricValue(value: number | null, unit: string | null): string {
  if (value === null || value === undefined) return '—'
  const rounded = value > 1000 ? Math.round(value).toLocaleString() : value.toLocaleString()
  return unit ? `${rounded}${unit}` : rounded
}

export function formatDollarCompact(value: number | null, symbol?: string | null): string {
  const sym = symbol || '$'
  if (value === null || value === undefined) return '—'
  if (value >= 1_000_000_000) return `${sym}${(value / 1_000_000_000).toFixed(1)}bn`
  if (value >= 1_000_000) return `${sym}${(value / 1_000_000).toFixed(0)}m`
  if (value >= 1_000) return `${sym}${(value / 1_000).toFixed(0)}k`
  return `${sym}${value}`
}

export function formatExposedPct(pct: number): string {
  return `${Math.round(pct)}%`
}

/**
 * Formats a value based on the metric type.
 * Used in charts and stats to display values consistently.
 */
export interface FormattableMetricOption {
  metric: string
  type: 'percentage' | 'total'
}

export function formatRiskProfileValue(
  value: number,
  metricOption: FormattableMetricOption,
  unit?: string | null,
): string {
  value = value || 0

  if (metricOption.type === 'percentage' || metricOption.metric === 'damage_severity') {
    const formattedValue =
      Math.abs(value) >= 1_000_000
        ? Intl.NumberFormat(undefined, {
            notation: 'compact',
            maximumFractionDigits: 1,
          }).format(value)
        : value.toLocaleString(undefined, {
            minimumSignificantDigits: 1,
            maximumSignificantDigits: 3,
          })

    return `${formattedValue}%`
  }

  if (metricOption.metric === 'damage_cost' || metricOption.metric === 'replacement_cost') {
    return formatDollarCompact(value, unit)
  }

  const formattedValue = value.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })

  return unit ? `${formattedValue} ${unit}` : formattedValue
}

export const getNumericValue = (value: unknown): number | null => {
  if (typeof value === 'number') return value

  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

export const getReplacementCost = (attributeStats: unknown): number | null => {
  if (!attributeStats || typeof attributeStats !== 'object') return null

  const replacementCosts = (attributeStats as Record<string, unknown>).replacement_costs
  if (!replacementCosts || typeof replacementCosts !== 'object') return null

  const costs = replacementCosts as Record<string, unknown>

  const candidates = [
    costs.total,
    (costs.total as Record<string, unknown> | undefined)?.value,
    costs.value,
    costs.sum,
    (costs.sum as Record<string, unknown> | undefined)?.value,
    costs.replacement_cost,
    (costs.replacement_cost as Record<string, unknown> | undefined)?.value,
  ]

  for (const candidate of candidates) {
    const value = getNumericValue(candidate)
    if (value !== null) return value
  }

  return null
}

export const formatChartLabel = (params: { value?: unknown }) => {
  const value = getNumericValue(params.value)

  return value != null && value >= 100 ? value.toLocaleString() : ''
}

/**
 * Parses a URLSearchParams key that may contain comma-separated values into an array of strings.
 * Returns an empty array if the key is not present or the value is an empty string.
 */
export const parseArrayParam = (params: URLSearchParams, key: string): string[] => {
  const value = params.get(key)
  return value !== null && value !== '' ? value.split(',') : []
}

export interface ProfileSubtitleInput {
  areaGroup: string
  areas: string[]
  analysisMode: 'by-group' | 'by-type'
  selectedBuckets: ElementGroupBucket[]
}

export const getPageTitle = ({
  areaGroup,
  areas,
  analysisMode,
  selectedBuckets,
}: ProfileSubtitleInput): string => {
  let titleParts: string[] = []
  if (selectedBuckets.length > 0) {
    const groupNames = selectedBuckets.map((bucket) => Object.keys(bucket)[0])
    if (groupNames.length === 1) titleParts.push(`${groupNames[0]}`)
    else if (groupNames.length <= 2) titleParts.push(`${groupNames.join(', ')}`)
    else titleParts.push(` Built Capital `)
  } else {
    titleParts.push('Built Capital')
  }
  if (!areaGroup) titleParts.push('for whole region')
  else if (areas.length === 0) titleParts.push(`for all ${areaGroup}`)
  else if (areas.length <= 2) titleParts.push(`for ${areas.join(', ')}`)
  else titleParts.push(`for ${areas.length} ${areaGroup}`)
  return titleParts.join(' ')
}

/**
 * Builds the region filter payload for risk profile API requests.
 * Uses explicit selected areas when available, otherwise falls back to the area group.
 * Returns an empty object when neither value is present.
 */
export const resolveRegionFilters = (
  areas: string[],
  areaGroup: string | null,
): { filters: { regions: string | string[] } } | {} => {
  const regions = areas.length > 0 ? areas : (areaGroup ?? null)

  return regions !== null ? { filters: { regions } } : {}
}

export interface FormattedValueParts {
  value: string
  unit: string | null
}

export function splitFormattedValue(
  formattedValue: string,
  unit: string | null,
): FormattedValueParts {
  if (!unit) {
    return {
      value: formattedValue,
      unit: null,
    }
  }

  const escapedUnit = unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const unitPattern = new RegExp(`\\s*${escapedUnit}\\s*$`, 'i')

  if (!unitPattern.test(formattedValue)) {
    return {
      value: formattedValue,
      unit: null,
    }
  }

  return {
    value: formattedValue.replace(unitPattern, '').trim(),
    unit,
  }
}

export function getFormattedValueParts(
  value: number,
  metricOption: FormattableMetricOption,
  unit: string | null,
): FormattedValueParts {
  const formattedValue = formatRiskProfileValue(value, metricOption, unit)

  const shouldSplitUnit =
    metricOption.metric !== 'damage_cost' &&
    metricOption.metric !== 'replacement_cost' &&
    metricOption.type !== 'percentage' &&
    metricOption.metric !== 'damage_severity'

  if (!shouldSplitUnit) {
    return {
      value: formattedValue,
      unit: null,
    }
  }

  return splitFormattedValue(formattedValue, unit)
}

export const DAMAGE_STATE_ORDER: Record<DamageState, number> = {
  exposed: 0,
  major: 1,
  moderate: 2,
  minor: 3,
  insignificant: 4,
  unknown: 5,
}

export const DAMAGE_STATE_ORDER_STRINGS = Object.keys(DAMAGE_STATE_ORDER)

export function orderDamageStates(damageStates: DamageState[]) {
  return [...damageStates].sort((a, b) => DAMAGE_STATE_ORDER[a] - DAMAGE_STATE_ORDER[b])
}
